/**
 * v2 refresh API のレスポンス。
 *
 * temporary handoff は `{ access_token, refresh_token }` を返す一方、
 * refresh は `{ access, refresh }` を返すため、nuxt-auth 標準の pointer には
 * 寄せずにこの composable で明示的に扱う。
 */
type V2RefreshResponse = {
  access: string
  refresh: string
}

let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null
/** 失敗した refresh token。同じ token での再試行を抑止する(signIn 後の新 token は通す) */
let failedRefreshToken: string | null = null

export const clearAuthState = () => {
  const { clearToken, rawRefreshToken, data } = useAuthState()
  clearToken()
  rawRefreshToken.value = null
  data.value = null
}

/**
 * POST /auth/v2/token/refresh を呼び出す。
 * API仕様: request { refresh }, response { access, refresh }
 * @see https://github.com/xmart-corp/xmart-web-api/pull/5942
 */
export const executeRefresh = (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const { rawRefreshToken, rawToken } = useAuthState()
  const currentRefresh = rawRefreshToken.value
  if (!currentRefresh || currentRefresh === failedRefreshToken) {
    return Promise.resolve(false)
  }

  const runtimeConfig = useRuntimeConfig()
  const orderingApiUrl = `${runtimeConfig.public.ORDERING_API_URL}/`

  isRefreshing = true
  refreshPromise = (() => {
    return $fetch<V2RefreshResponse>('auth/v2/token/refresh', {
      baseURL: orderingApiUrl,
      method: 'POST',
      body: { refresh: currentRefresh },
    })
      .then((response) => {
        failedRefreshToken = null
        rawToken.value = response.access
        rawRefreshToken.value = response.refresh
        return true
      })
      .catch(() => {
        failedRefreshToken = currentRefresh
        clearAuthState()
        return false
      })
      .finally(() => {
        isRefreshing = false
        refreshPromise = null
      })
  })()

  return refreshPromise
}
